/**
 * Standard API search response structure from the server.
 */
export interface SearchApiResponse<T = unknown> {
  data?: {
    documents?: T[]
    total_documents?: number
  }
}

/**
 * Transformed search result with pagination info.
 */
export interface SearchResult<T> {
  items: T[]
  total: number
  totalPages: number
}

/**
 * Transform a standard search API response into a consistent format with pagination.
 *
 * @example
 * // In a useQuery hook:
 * return useQuery({
 *   queryKey: contactsKeys.list(params),
 *   queryFn: () => api.contacts.search(params),
 *   select: (response) => transformSearchResponse(response, ITEMS_PER_PAGE),
 * })
 *
 * @example
 * // With custom item key name:
 * select: (response) => {
 *   const { items, total, totalPages } = transformSearchResponse<Contact>(response, 15)
 *   return { contacts: items, total, totalPages }
 * }
 */
export function transformSearchResponse<T>(
  response: SearchApiResponse<T>,
  itemsPerPage: number
): SearchResult<T> {
  const documents = response.data?.documents || []
  const totalDocuments = response.data?.total_documents || 0

  return {
    items: documents,
    total: totalDocuments,
    totalPages: Math.ceil(totalDocuments / itemsPerPage),
  }
}

/**
 * Extract just the documents array from a search response.
 * Useful when you don't need pagination info.
 *
 * @example
 * select: (response) => extractDocuments<Lead>(response)
 */
export function extractDocuments<T>(response: SearchApiResponse<T>): T[] {
  return response.data?.documents || []
}

/**
 * Extract an array from various API response formats.
 * Handles these structures:
 * - Direct array: `[...]`
 * - Single wrapped: `{ data: [...] }`
 * - Search format: `{ data: { documents: [...] } }`
 * - Named arrays: `{ tasks: [...] }`, `{ documents: [...] }`
 *
 * @example
 * // In a queryFn:
 * const items = extractArray<Task>(await api.tasks.find())
 *
 * @example
 * // With search API:
 * const items = extractArray<Matter>(await api.matters.search(params))
 */
export function extractArray<T>(response: unknown): T[] {
  if (Array.isArray(response)) return response
  if (response && typeof response === "object") {
    const res = response as Record<string, unknown>
    // Check doubly-nested structure first (search response format)
    if (res.data && typeof res.data === "object" && !Array.isArray(res.data)) {
      const inner = res.data as Record<string, unknown>
      if (Array.isArray(inner.documents)) return inner.documents as T[]
    }
    // Single-nested structures
    if (Array.isArray(res.data)) return res.data as T[]
    if (Array.isArray(res.documents)) return res.documents as T[]
    if (Array.isArray(res.tasks)) return res.tasks as T[]
  }
  return []
}

/**
 * Extract the total count from various API response formats.
 * Handles these structures:
 * - Search format: `{ data: { total_documents: number } }`
 * - Direct: `{ total_documents: number }`
 *
 * @example
 * const total = extractTotal(response)
 */
export function extractTotal(response: unknown): number {
  if (response && typeof response === "object") {
    const res = response as Record<string, unknown>
    // Check nested structure first (search response format)
    if (res.data && typeof res.data === "object") {
      const inner = res.data as Record<string, unknown>
      if (typeof inner.total_documents === "number") return inner.total_documents
    }
    // Direct structure
    if (typeof res.total_documents === "number") return res.total_documents
  }
  return 0
}

/**
 * Extract a single data object from an API response.
 * Handles `{ data: T }` format.
 *
 * @example
 * const matter = extractData<Matter>(response)
 */
export function extractData<T>(response: unknown): T | null {
  if (response && typeof response === "object") {
    const res = response as Record<string, unknown>
    if (res.data !== undefined) return res.data as T
  }
  return null
}

/**
 * Paginated response structure returned by transformPaginatedResponse.
 */
export interface PaginatedResponse<T> {
  items: T[]
  total: number
  totalPages: number
}

/**
 * Transform any API response into a paginated result.
 * More flexible than transformSearchResponse - handles various response formats.
 *
 * @example
 * // In a useQuery select:
 * select: (response) => {
 *   const result = transformPaginatedResponse<Matter>(response, ITEMS_PER_PAGE)
 *   return { matters: result.items, total: result.total, totalPages: result.totalPages }
 * }
 */
export function transformPaginatedResponse<T>(
  response: unknown,
  itemsPerPage: number
): PaginatedResponse<T> {
  const items = extractArray<T>(response)
  const total = extractTotal(response)
  return {
    items,
    total,
    totalPages: itemsPerPage > 0 ? Math.ceil(total / itemsPerPage) : 0,
  }
}
